import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { ArrowRight, ChevronDown, Plus } from 'react-feather';
import { useTranslation } from 'react-i18next';
import React from 'react';
import styled, { css } from 'styled-components';

import { assetUrl } from '../../../../common/helpers';
import { mix } from '../../../../theme/helpers';
import SC from '../../../../common/UI/SC';
import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';

interface CheckoutTabProps {
  active?: boolean;
  disabled?: boolean;
}

const CheckoutTab = motion(styled.span<SC<CheckoutTabProps>>`
  ${({ sc: { active, disabled } = {}, theme }) => css`
    display: inline-block;
    position: relative;
    font-weight: 500;
    color: ${theme.colors.gray[700]};
    border-radius: ${theme.borderRadiuses.md}px;
    font-size: ${12 / 14}rem;
    opacity: 0.8;
    transition: all 0.15s ease-in-out;
    animation: fade-in 0.25s ease-in-out;
    text-align: center;

    ${active && css`
      color: ${mix(theme.colors.secondary[800], theme.colors.gray[900], 0.5)};

      ${CheckoutBullet} {
        background: ${theme.colors.secondary[500]};
        color: ${theme.colors.white};
      }
    `}

    ${disabled && css`
      ${CheckoutBullet} {
        color: ${mix(theme.colors.secondary[400], theme.colors.gray[400], 0.6)};
      }
    `}
  `}
`);

const CheckoutBullet = styled(UI.Span)`
  ${({ theme }) => css`
    display: inline-block;
    width: 2em;
    height: 2em;
    border-radius: 100%;
    text-align: center;
    line-height: 2em;
    background: ${mix(theme.colors.secondary[200], theme.colors.gray[300], 0.3)};
    color: ${mix(theme.colors.secondary[800], theme.colors.gray[800], 0.6)};
    transition: all 0.15s ease-in-out;
  `}
`;

const CheckoutTabs = styled(UI.Div)`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: 50px 1fr 50px;
    align-items: center;
    background: ${mix(theme.colors.secondary[150], theme.colors.gray[200], 0.3)};
    color: ${mix(theme.colors.secondary[300], theme.colors.gray[400], 0.5)};
    padding: ${theme.gutter / 2}px ${theme.gutter}px ${theme.gutter / 2 + 10}px ${theme.gutter}px;
    margin-bottom: -10px;
    z-index: -1;
    white-space: nowrap;
    font-size: 0.75rem;

    @media (min-width: 501px) {
      border-top-left-radius: ${theme.borderRadiuses.xl}px;
      border-top-right-radius: ${theme.borderRadiuses.xl}px;
    }
  `}
`;

const CheckoutCard = styled(UI.Card)`
  ${({ sc: { hasPanels } = {}, theme }) => css`
    overflow: hidden;

    ${UI.H4} {
      font-size: ${12 / 14}rem;
    }

    @media (max-width: 500px) {
      ${!hasPanels && css`
        padding: ${theme.gutter * 0.75}px !important;
      `}

      ${hasPanels && css`
        > * {
          padding: ${theme.gutter * 0.75}px !important;
        }
      `}
    }

    @media (min-width: 501px) {
      border-radius: ${theme.borderRadiuses.xl}px;

      ${!hasPanels && css`
        padding: ${theme.gutter}px !important;
      `}

      ${hasPanels && css`
        > * {
          padding: ${theme.gutter}px !important;
        }
      `}
    }
  `}
`;

const MotionLegend = motion(styled(UI.Legend)`
  font-size: 0.75rem !important;
  opacity: 0.75 !important;
`);

const MotionCard = motion(UI.Card);

const MotionBoxContainer = motion(styled.div`
  height: 284px;
`);

const MotionBox = ({ children }: { children: React.ReactNode }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <MotionBoxContainer>
      {children}
    </MotionBoxContainer>
  </motion.div>
);

interface PreviewCheckoutProps {
  isActive?: boolean;
  translationKey?: keyof typeof ticketOnePrice;
}

const PreviewCheckout = ({ isActive, translationKey = 'cycling' }: PreviewCheckoutProps) => {
  const { t } = useTranslation('common');
  const { formatCurrency } = useLocale();

  return (
    <LayoutGroup id="PreviewCheckoutLayout">
      <UI.Div key="isActive" sc={{ width: ['100%', 'auto'] }}>
        <CheckoutTabs>
          <span />
          <CheckoutTab
            layout
            layoutId="ticketsTab"
            sc={{ active: !isActive }}
          >
            {t('checkout_preview.tickets')}
          </CheckoutTab>
          <span style={{ textAlign: 'right' }}>
            1 / 3
          </span>
        </CheckoutTabs>
        <CheckoutCard sc={{ hasPanels: true }}>
          <UI.Div>
            <AnimatePresence mode="wait" initial={false}>
              {isActive ? (
                <MotionLegend
                  key="isActiveLegend"
                  exit={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  initial={{ opacity: 0 }}
                  layoutId="chooseLegend"
                  sc={{ mb: 3 }}
                >
                  {t('checkout_preview.choose_your_products')}
                </MotionLegend>
              ) : (
                <MotionLegend
                  key="isNonActiveLegend"
                  exit={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  initial={{ opacity: 0 }}
                  layoutId="chooseLegend"
                  sc={{ mb: 3 }}
                >
                  {t('checkout_preview.choose_your_tickets')}
                </MotionLegend>
              )}
            </AnimatePresence>
            <AnimatePresence mode="wait" initial={false}>
              {isActive ? (
                <MotionBox key="isActiveProducts">
                  <MotionCard
                    sc={{ outline: 'gray.200', mb: 0, flex: { direction: 'column' } }}
                    style={{
                      borderWidth: 1,
                      padding: 4,
                      height: '100%',
                    }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 1 }}
                  >
                    <UI.Div sc={{ padding: 2 }}>
                      <UI.Image sc={{ ratio: 3, flexSelf: '100%' }} style={{ borderRadius: 2 }}>
                        <UI.Div>
                          <img
                            src={assetUrl(`images/feature-image-${translationKey}.jpg`)}
                            alt={t(`checkout_preview.product.${translationKey}`)}
                          />
                        </UI.Div>
                      </UI.Image>
                    </UI.Div>
                    <UI.Div sc={{ padding: 2 }}>
                      <UI.Div sc={{ mb: 0.5 }}>
                        <UI.Strong>{t(`checkout_preview.product.${translationKey}`)}</UI.Strong>
                      </UI.Div>
                      <UI.Div sc={{ mb: 2 }}>
                        {formatCurrency(productPrice[translationKey])}
                      </UI.Div>
                      <UI.FlexContainer
                        sc={{ justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <UI.Button style={{ pointerEvents: 'none' }} sc={{ brand: 'secondary' }}>
                          {t('checkout_preview.choose')}
                          <UI.Icon sc={{ ml: 2 }}>
                            <ChevronDown />
                          </UI.Icon>
                        </UI.Button>
                        <UI.Div sc={{ textAlign: 'right', muted: true, noWrap: true }}>
                          {t('checkout_preview.n_options', { count: 3 })}
                        </UI.Div>
                      </UI.FlexContainer>
                    </UI.Div>
                  </MotionCard>
                </MotionBox>
              ) : (
                <MotionBox key="isNotActiveTickets">
                  <UI.GridContainer sc={{ gutter: 0.75 }}>
                    <UI.Card
                      sc={{ outline: 'gray.200', mb: 0, padding: 3 }}
                      style={{
                        borderWidth: 1,
                      }}
                    >
                      <UI.Div sc={{ mb: 2 }}>
                        <UI.H4 sc={{ mb: 0.5 }}>
                          {t(`checkout_preview.ticket_one.${translationKey}`)}
                        </UI.H4>
                        <UI.Span>
                          {formatCurrency(ticketOnePrice[translationKey])}
                        </UI.Span>
                      </UI.Div>
                      <UI.Button style={{ pointerEvents: 'none' }} sc={{ brand: 'secondary' }}>
                        <UI.Icon>
                          <Plus />
                        </UI.Icon>
                        {' '}
                        {t('checkout_preview.add')}
                      </UI.Button>
                    </UI.Card>
                    <UI.Card
                      sc={{ outline: 'secondary', mb: 0, padding: 3 }}
                      style={{ borderWidth: 2 }}
                    >
                      <UI.Div sc={{ mb: 2 }}>
                        <UI.H4 sc={{ mb: 0.5 }}>
                          {t(`checkout_preview.ticket_two.${translationKey}`)}
                        </UI.H4>
                        <UI.Span>
                          {formatCurrency(ticketTwoPrice[translationKey])}
                        </UI.Span>
                      </UI.Div>
                      <UI.Div style={{ width: 150, pointerEvents: 'none' }}>
                        <UI.QuantitySelect quantity={2} onChange={() => null} maxQuantity={3} />
                      </UI.Div>
                    </UI.Card>
                  </UI.GridContainer>
                </MotionBox>
              )}
            </AnimatePresence>
          </UI.Div>

          <UI.Div>
            <UI.FlexContainer sc={{ justifyContent: 'space-between', mb: 4 }}>
              <UI.Span>{t('checkout_preview.total')}</UI.Span>
              <UI.Strong>{formatCurrency(totalPrice[translationKey])}</UI.Strong>
            </UI.FlexContainer>
            <UI.Button
              style={{ pointerEvents: 'none' }}
              sc={{ brand: 'secondary', width: '100%', size: 'md' }}
            >
              {t('checkout_preview.next')}
              {' '}
              <UI.Icon sc={{ mr: 2 }}>
                <ArrowRight />
              </UI.Icon>
            </UI.Button>
          </UI.Div>
        </CheckoutCard>
      </UI.Div>
    </LayoutGroup>
  );
};

export const PreviewCheckoutBackground = styled(UI.Div)`
  ${({ theme }) => css`
    background: ${theme.colors.gray[75]};
    display: flex;
    justify-content: center;
  `}
`;

const ticketOnePrice = {
  default: 1500,
  cycling: 1500,
  running: 1750,
  triathlon: 1750,
  walking: 1000,
};

const ticketTwoPrice = {
  default: 1500,
  cycling: 2000,
  running: 2250,
  triathlon: 2750,
  walking: 1500,
};

const productPrice = {
  default: 995,
  cycling: 995,
  running: 1995,
  triathlon: 1995,
  walking: 995,
};

const totalPrice = {
  default: 3000,
  cycling: 4000,
  running: 4500,
  triathlon: 5500,
  walking: 3000,
};

export default PreviewCheckout;
